import { useState, useMemo } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { AccordionItem, AccordionTrigger, AccordionContent } from "@/ui/accordion";
import { Spinner } from "@/ui/spinner";
import { sectionDetailQuery } from "@/lib/queries";
import { ElementRow } from "./ElementRow";
import { ElementGroupRow } from "./ElementGroupRow";
import { Empty, EmptyDescription } from "@/ui/empty";
import { Badge } from "@/ui/badge";
import { useProjectContext } from "../context/ProjectContext";
import { useAddGroupItem } from "@/api/useGroups";
import { cn } from "@/lib/utils";

interface SectionRowProps {
    section: any;
    projectId: string;
    searchTerm: string;
    selectedPage: string | null;
}

export function SectionRow({ section, projectId, searchTerm, selectedPage }: SectionRowProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const { pendingEdits } = useProjectContext();

  const addItemMutation = useAddGroupItem();

  const handleAddItem = (groupId: string, values: Record<string, string>) => {
    addItemMutation.mutate({ projectId, groupId, values });
  };

  // Prefetch on hover
  const onMouseEnter = () => {
    queryClient.prefetchQuery(sectionDetailQuery(projectId, section.id));
  };

  // Fetch only when open
  const { data: sectionDetail } = useQuery({
    ...sectionDetailQuery(projectId, section.id),
    enabled: isOpen,
  });

  // Use the fetched details - now returns ungroupedElements and groups separately
  const ungroupedElements = sectionDetail?.section?.ungroupedElements || [];
  const groups = sectionDetail?.section?.groups || [];

  // Count local pending edits for this section
  const localEditCount = Array.from(pendingEdits.values()).filter(
    edit => edit.sectionId === section.id
  ).length;

  // Count of elements with pending PRs (from API)
  const pendingPRCount = section.pendingEditCount || 0;

  // Filter ungrouped elements by search term and selected page
  const filteredUngroupedElements = useMemo(() => {
    return ungroupedElements.filter((el: any) => {
      if (selectedPage !== null && el.pageUrl !== selectedPage) return false;
      if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase();
        const matchesValue = (el.currentValue || "").toLowerCase().includes(lowerSearch);
        const matchesName = (el.name || "").toLowerCase().includes(lowerSearch);
        if (!matchesValue && !matchesName) return false;
      }
      return true;
    });
  }, [ungroupedElements, searchTerm, selectedPage]);

  // Filter groups - keep groups that have at least one matching element
  const filteredGroups = useMemo(() => {
    return groups.map((group: any) => {
      const filteredItems = group.compoundItems.filter((item: any) => {
        return item.elements.some((el: any) => {
          if (selectedPage !== null && el.pageUrl !== selectedPage) return false;
          if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            const matchesValue = (el.currentValue || "").toLowerCase().includes(lowerSearch);
            const matchesName = (el.name || "").toLowerCase().includes(lowerSearch);
            if (!matchesValue && !matchesName) return false;
          }
          return true;
        });
      });
      return { ...group, compoundItems: filteredItems };
    }).filter((group: any) => group.compoundItems.length > 0);
  }, [groups, searchTerm, selectedPage]);

  const hasContent = filteredUngroupedElements.length > 0 || filteredGroups.length > 0;
  const isLoading = isOpen && ungroupedElements.length === 0 && groups.length === 0;

  return (
    <AccordionItem
      value={section.id}
      className={cn(
        localEditCount > 0 && "border-l-2 border-l-primary",
        pendingPRCount > 0 && localEditCount === 0 && "border-l-2 border-l-amber-500"
      )}
    >
      <AccordionTrigger
        onMouseEnter={onMouseEnter}
        onClick={() => setIsOpen(true)}
      >
        <div className="text-left flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{section.name}</span>
            {localEditCount > 0 && (
              <Badge variant="default" className="text-[10px] bg-primary/20 text-primary">
                {localEditCount} edit{localEditCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {pendingPRCount > 0 && (
              <Badge variant="warning" className="text-[10px]">
                {pendingPRCount} pending
              </Badge>
            )}
          </div>
          <div className="text-muted-foreground text-xs">
            {section.elementCount} components
            {section.groupCount > 0 &&
              `, ${section.groupCount} list${section.groupCount !== 1 ? 's' : ''}`}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-2 pt-2">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground text-xs">
              <Spinner className="size-3" />
              Loading elements...
            </div>
          ) : !hasContent ? (
            <Empty className="py-4">
              <EmptyDescription>
                {selectedPage
                  ? "No elements on this page matching your filters."
                  : "No elements matching search."}
              </EmptyDescription>
            </Empty>
          ) : (
            <>
              {/* Render ungrouped elements */}
              {filteredUngroupedElements.map((element: any) => (
                <ElementRow
                  key={`${element.id}-${element.currentValue}`}
                  element={element}
                  sectionId={section.id}
                  sectionName={section.name}
                  projectId={projectId}
                />
              ))}

              {/* Render groups with pre-grouped compound items */}
              {filteredGroups.map((group: any) => (
                <ElementGroupRow
                  key={group.id}
                  group={group}
                  sectionId={section.id}
                  sectionName={section.name}
                  projectId={projectId}
                  onAddItem={handleAddItem}
                />
              ))}
            </>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
